import React, { useState, useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import { useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ResponsiveImage, IconCloseNew } from '../../../components';
import { withViewport } from '../../../util/uiHelpers';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';
import ListingImageGalleryMobileHero from './ListingImageGalleryMobileHero';
import ListingImageGalleryItem from './ListingImageGalleryItem';
import { createGalleryRenderers } from './ListingImageGalleryRenderers';
import ListingImageGalleryDesktopGrid from './ListingImageGalleryDesktopGrid';

const MAX_MOBILE_SCREEN_WIDTH = 768;

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const {
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    viewport,
    currentUser,
    listing,
    onToggleFavorites,
    onManageDisableScrolling = () => {},
    optimizedImages,
    ssrIsMobile,
  } = props;

  const [isCopied, setIsCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNav, setShowNav] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [index, setIndex] = useState(0);
  const galleryRef = useRef(null);
  const galleryContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const didSwipeRef = useRef(false);
  const intl = useIntl();

  const { width } = viewport || {};
  const isMobile = width > 0 ? width <= MAX_MOBILE_SCREEN_WIDTH : (ssrIsMobile ?? false);
  const totalImages = images?.length || 0;
  const isMobileHero = isMobile && !isFullscreen;
  const isDesktopHero = !isMobile && !isFullscreen;

  const thumbVariants = thumbnailVariants || imageVariants;
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const heroAspectWidth = isMobileHero ? 4 : aspectWidth || 1;
  const heroAspectHeight = isMobileHero ? 3 : aspectHeight || 1;

  const mobileHeroImages = images.slice(0, 5);
  const galleryImages = isFullscreen ? images : isMobile ? mobileHeroImages : images.slice(0, 1);

  // Check for video
  const videoUrl = listing?.attributes?.metadata?.videos?.mergedVideo?.url;
  const hasVideo = !!videoUrl;
  const firstImage = galleryImages?.[0];
  const videoThumbnail = firstImage?.attributes?.variants?.[thumbVariants[0]];

  // Build items array - video first if available, then images
  const imageItems = galleryImages.map((img, i) => {
    return {
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltTextDel' },
        { index: i + 1, count: images.length + (videoUrl ? 1 : 0) }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltTextDel' },
        { index: i + 1, count: images.length + (videoUrl ? 1 : 0) }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      index: i + (hasVideo ? 1 : 0), // Adjust index if video is first
      imageIndex: i, // Raw image array index for optimizedImages lookup
      isVideo: false,
    };
  });

  const videoItem = hasVideo
    ? {
        original: '',
        alt: intl.formatMessage({ id: 'ListingImageGallery.videoAltText' }),
        thumbAlt: intl.formatMessage({ id: 'ListingImageGallery.videoThumbnailAltText' }),
        thumbnail: videoThumbnail,
        videoUrl: videoUrl,
        image: firstImage, // Use first image as fallback/thumbnail
        index: 0,
        isVideo: true,
      }
    : null;

  const items = videoItem ? [videoItem, ...imageItems] : imageItems;
  const totalMediaItems = items.length;

  const handleThumbnailClick = useCallback(
    (_, index) => {
      if (isZoomed) {
        setIsZoomed(false);
      }
      if (thumbnailRefs.current[index]) {
        thumbnailRefs.current[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    },
    [thumbnailRefs, isZoomed]
  );

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('gallery-fullscreen-open');
    } else {
      document.body.classList.remove('gallery-fullscreen-open');
    }
    return () => {
      document.body.classList.remove('gallery-fullscreen-open');
    };
  }, [isFullscreen]);

  const openFullscreen = useCallback(() => {
    if (galleryRef.current) {
      if (typeof galleryRef.current.slideToIndex === 'function') {
        galleryRef.current.slideToIndex(index);
      }
      setIsZoomed(false);
      galleryRef.current.fullScreen();
    }
  }, [index]);

  const openFullscreenAtIndex = useCallback(
    nextIndex => {
      if (!galleryRef.current) return;
      if (typeof galleryRef.current.slideToIndex === 'function') {
        galleryRef.current.slideToIndex(nextIndex);
      }
      setIndex(nextIndex);
      setIsZoomed(false);
      galleryRef.current.fullScreen();
    },
    [setIndex]
  );

  const handleMobileHeroIndexChange = useCallback(nextIndex => {
    setIndex(nextIndex);
    didSwipeRef.current = true;
  }, []);

  const renderItem = item => {
    const imageSizesMaybe = isFullscreen
      ? {}
      : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
    const heroPriorityProps =
      !isFullscreen && item.index === 0
        ? { loading: 'eager', fetchPriority: 'high', decoding: 'async' }
        : {};

    return (
      <ListingImageGalleryItem
        item={item}
        aspectWidth={heroAspectWidth}
        aspectHeight={heroAspectHeight}
        isFullscreen={isFullscreen}
        setShowNav={setShowNav}
        isMobile={isMobile}
        totalImages={totalMediaItems}
        openFullscreen={openFullscreen}
        index={index}
        imageVariants={imageVariants}
        imageSizesMaybe={imageSizesMaybe}
        heroPriorityProps={heroPriorityProps}
        intl={intl}
        didSwipeRef={didSwipeRef}
        onExitFullscreen={() => galleryRef.current.exitFullScreen()}
        isZoomed={isZoomed}
        onZoomChange={setIsZoomed}
        optimizedImages={optimizedImages}
      />
    );
  };

  const { renderThumbInner, renderLeftNav, renderRightNav } = createGalleryRenderers({
    items,
    galleryRef,
    showNav,
    setShowNav,
    handleThumbnailClick,
    thumbVariants,
    thumbnailRefs,
    optimizedImages,
  });

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const renderCustomControls =
    isFullscreen && isMobile
      ? () => (
          <div className={css.fullscreenMobileHeader}>
            <button
              type="button"
              className={css.fullscreenMobileClose}
              aria-label="Close gallery"
              onClick={() => galleryRef.current?.exitFullScreen?.()}
            >
              <IconCloseNew className={css.fullscreenMobileCloseIcon} />
            </button>
            <div className={css.fullscreenMobileCounter}>
              {index + 1} / {totalMediaItems}
            </div>
          </div>
        )
      : null;

  return (
    <div ref={galleryContainerRef}>
      {isMobileHero ? (
        <ListingImageGalleryMobileHero
          images={images}
          imageVariants={imageVariants}
          aspectWidth={heroAspectWidth}
          aspectHeight={heroAspectHeight}
          index={index}
          onIndexChange={handleMobileHeroIndexChange}
          onOpenFullscreen={openFullscreen}
          totalImages={totalImages}
          videoUrl={videoUrl}
          intl={intl}
          currentUser={currentUser}
          listing={listing}
          onToggleFavorites={onToggleFavorites}
          optimizedImages={optimizedImages}
        />
      ) : null}
      {isDesktopHero ? (
        <ListingImageGalleryDesktopGrid
          images={images}
          imageVariants={imageVariants}
          aspectWidth={heroAspectWidth}
          aspectHeight={heroAspectHeight}
          totalImages={totalImages}
          videoUrl={videoUrl}
          onOpenFullscreenAtIndex={openFullscreenAtIndex}
          onOpenFullscreen={openFullscreen}
          intl={intl}
          currentUser={currentUser}
          listing={listing}
          onToggleFavorites={onToggleFavorites}
          optimizedImages={optimizedImages}
        />
      ) : null}
      <ReactImageGallery
        ref={galleryRef}
        additionalClass={classNames(
          rootClassName || css.root,
          className,
          isFullscreen && isMobile ? css.fullscreenMobileGallery : null,
          isFullscreen && isMobile && isZoomed ? css.fullscreenMobileZoomed : null,
          isMobileHero || isDesktopHero ? css.hiddenGallery : null
        )}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onThumbnailClick={handleThumbnailClick}
        onScreenChange={fullScreen => {
          setIsFullscreen(fullScreen);
          if (!fullScreen) {
            setIsZoomed(false);
          }
          if (!fullScreen && galleryRef.current) {
            setIndex(0);
            if (typeof galleryRef.current.slideToIndex === 'function') {
              galleryRef.current.slideToIndex(0);
            }
          }
        }}
        showFullscreenButton={false}
        showThumbnails={isFullscreen && items.length > 1}
        showNav={isFullscreen && items.length > 1}
        showBullets={false}
        showIndex={false}
        renderCustomControls={renderCustomControls}
        disableSwipe={isZoomed || (!isFullscreen && !isMobile)}
        disableKeyDown={!isFullscreen}
        infinite={!isMobileHero}
        preventDefaultTouchmoveEvent={false}
        onSlide={slideIndex => {
          if (isZoomed) {
            return;
          }
          setIndex(slideIndex);
          setIsZoomed(false);
          if (thumbnailRefs.current[slideIndex]) {
            thumbnailRefs.current[slideIndex].scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }}
        {...IMAGE_GALLERY_OPTIONS}
      />
    </div>
  );
};

export default withViewport(ListingImageGallery);
